import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Alert,
  Checkbox,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import { Close, Upload, Schedule } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Business,
  CreateBusinessRequest,
  UpdateBusinessRequest,
  BUSINESS_CATEGORIES,
  BusinessHours,
} from '@/constants/types';
import { uploadService } from '@/services/upload/uploadService';
import toast from 'react-hot-toast';
import { getFileUrl, getFileId, getFileName } from '@/utils/businessHelpers';

interface BusinessDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateBusinessRequest | UpdateBusinessRequest) => void;
  business?: Business | null;
  isLoading?: boolean;
}

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const validationSchema = Yup.object({
  businessName: Yup.string()
    .required('Business name is required')
    .min(2, 'Business name must be at least 2 characters')
    .max(100, 'Business name must not exceed 100 characters'),
  businessUsername: Yup.string()
    .required('Business username is required')
    .matches(/^@[a-zA-Z0-9_]{3,30}$/, 'Username must start with @ and contain 3-30 alphanumeric characters or underscores'),
  businessCategory: Yup.string().required('Business category is required'),
  description: Yup.string().max(1000, 'Description must not exceed 1000 characters'),
  email: Yup.string()
    .required('Email is required')
    .email('Please enter a valid email'),
  website: Yup.string().url('Please enter a valid website URL'),
  contactNumber: Yup.string()
    .required('Contact number is required')
    .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit contact number'),
  whatsappNumber: Yup.string()
    .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit WhatsApp number'),
  landlineNumber: Yup.string()
    .matches(/^[0-9-+() ]{6,20}$/, 'Please enter a valid landline number'),
  addressLine1: Yup.string()
    .required('Address line 1 is required')
    .max(100, 'Address line 1 must not exceed 100 characters'),
  addressLine2: Yup.string().max(100, 'Address line 2 must not exceed 100 characters'),
  city: Yup.string()
    .required('City is required')
    .max(50, 'City must not exceed 50 characters'),
  state: Yup.string()
    .required('State is required')
    .max(50, 'State must not exceed 50 characters'),
  pincode: Yup.string()
    .required('Pincode is required')
    .max(10, 'Pincode must not exceed 10 characters'),
  landmark: Yup.string().max(100, 'Landmark must not exceed 100 characters'),
  ownerName: Yup.string()
    .required('Owner name is required')
    .max(100, 'Owner name must not exceed 100 characters'),
  socialMedia: Yup.object({
    instagram: Yup.string().url('Please enter a valid Instagram URL'),
    facebook: Yup.string().url('Please enter a valid Facebook URL'),
    youtube: Yup.string().url('Please enter a valid YouTube URL'),
    twitter: Yup.string().url('Please enter a valid Twitter/X URL'),
  }),
});

export const BusinessDialog: React.FC<BusinessDialogProps> = ({
  open,
  onClose,
  onSubmit,
  business,
  isLoading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isEditMode = !!business;

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [logoId, setLogoId] = useState<string>('');
  const [ownerIdProofFile, setOwnerIdProofFile] = useState<File | null>(null);
  const [ownerIdProofPreview, setOwnerIdProofPreview] = useState<string>('');
  const [ownerIdProofId, setOwnerIdProofId] = useState<string>('');
  const [documents, setDocuments] = useState<Array<{ documentType: string; file?: File; fileId?: string; fileName: string }>>([]);
  const [businessHours, setBusinessHours] = useState<BusinessHours[]>(
    DAYS_OF_WEEK.map((day) => ({ day, openTime: '09:00', closeTime: '18:00', isClosed: false }))
  );
  const [uploading, setUploading] = useState(false);

  // Refs for file inputs to reset them
  const ownerIdProofInputRef = React.useRef<HTMLInputElement>(null);

  const formik = useFormik({
    initialValues: {
      businessName: '',
      businessUsername: '',
      businessCategory: '',
      description: '',
      email: '',
      website: '',
      contactNumber: '',
      whatsappNumber: '',
      landlineNumber: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      landmark: '',
      ownerName: '',
      socialMedia: {
        instagram: '',
        facebook: '',
        youtube: '',
        twitter: '',
      },
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setUploading(true);

        // Upload logo if new file is selected
        let finalLogoId = logoId;
        if (logoFile) {
          const logoUploadResponse = await uploadService.uploadSingle(logoFile, { folder: 'business/logos' });
          finalLogoId = logoUploadResponse.file.id;
        }

        // Upload owner ID proof if new file is selected
        let finalOwnerIdProofId = ownerIdProofId;
        if (ownerIdProofFile) {
          const idProofUploadResponse = await uploadService.uploadSingle(ownerIdProofFile, { folder: 'business/documents' });
          finalOwnerIdProofId = idProofUploadResponse.file.id;
        }

        // Upload all documents
        const uploadedDocuments: Array<{ documentType: string; fileId: string }> = [];
        for (const doc of documents) {
          if (doc.file) {
            // Upload new file
            const uploadResponse = await uploadService.uploadSingle(doc.file, { folder: 'business/documents' });
            uploadedDocuments.push({
              documentType: doc.documentType,
              fileId: uploadResponse.file.id,
            });
          } else if (doc.fileId) {
            // Keep existing file
            uploadedDocuments.push({
              documentType: doc.documentType,
              fileId: doc.fileId,
            });
          }
        }

        const businessData: any = {
          ...values,
          businessHours: businessHours,
        };

        // Add file IDs - in edit mode, always include to allow deletion
        if (isEditMode) {
          businessData.logoId = finalLogoId || null;
          businessData.ownerIdProofId = finalOwnerIdProofId || null;
          if (uploadedDocuments.length > 0) {
            businessData.documents = uploadedDocuments;
          } else {
            businessData.documents = [];
          }
        } else {
          // In create mode, only add if they exist
          if (finalLogoId) {
            businessData.logoId = finalLogoId;
          }
          if (finalOwnerIdProofId) {
            businessData.ownerIdProofId = finalOwnerIdProofId;
          }
          if (uploadedDocuments.length > 0) {
            businessData.documents = uploadedDocuments;
          }
        }

        if (isEditMode) {
          onSubmit(businessData as UpdateBusinessRequest);
        } else {
          onSubmit(businessData as CreateBusinessRequest);
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to upload files');
      } finally {
        setUploading(false);
      }
    },
  });

  useEffect(() => {
    if (open) {
      if (business) {
        formik.setValues({
          businessName: business.businessName,
          businessUsername: business.businessUsername,
          businessCategory: Array.isArray(business.businessCategory) ? business.businessCategory[0] || '' : business.businessCategory,
          description: business.description || '',
          email: business.email,
          website: business.website || '',
          contactNumber: business.contactNumber,
          whatsappNumber: business.whatsappNumber || '',
          landlineNumber: business.landlineNumber || '',
          addressLine1: business.addressLine1,
          addressLine2: business.addressLine2 || '',
          city: business.city,
          state: business.state,
          pincode: business.pincode,
          landmark: business.landmark || '',
          ownerName: business.ownerName,
          socialMedia: {
            instagram: business.socialMedia?.instagram || '',
            facebook: business.socialMedia?.facebook || '',
            youtube: business.socialMedia?.youtube || '',
            twitter: business.socialMedia?.twitter || '',
          },
        });

        // Extract file IDs and URLs
        const businessLogoId = getFileId(business.logoId);
        const businessOwnerIdProofId = getFileId(business.ownerIdProofId);

        setLogoId(businessLogoId);
        setLogoPreview(getFileUrl(business.logoId));
        setOwnerIdProofId(businessOwnerIdProofId);
        setOwnerIdProofPreview(getFileUrl(business.ownerIdProofId));

        // Map documents to include fileId and fileName (existing documents)
        const mappedDocs = (business.documents || []).map(doc => ({
          documentType: doc.documentType,
          fileId: getFileId(doc.fileId),
          fileName: getFileName(doc.fileId) || 'Document',
        }));
        setDocuments(mappedDocs);

        if (business.businessHours && business.businessHours.length > 0) {
          setBusinessHours(business.businessHours);
        }

        // Reset file input in edit mode (no new file selected yet)
        if (ownerIdProofInputRef.current) {
          ownerIdProofInputRef.current.value = '';
        }
      } else {
        formik.resetForm();
        setLogoFile(null);
        setLogoPreview('');
        setLogoId('');
        setOwnerIdProofFile(null);
        setOwnerIdProofPreview('');
        setOwnerIdProofId('');
        setDocuments([]);
        setBusinessHours(DAYS_OF_WEEK.map((day) => ({ day, openTime: '09:00', closeTime: '18:00', isClosed: false })));

        // Reset file inputs
        if (ownerIdProofInputRef.current) {
          ownerIdProofInputRef.current.value = '';
        }
      }
    }
  }, [open, business]);

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo file size must be less than 2MB');
        return;
      }

      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleOwnerIdProofChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Document file size must be less than 2MB');
        return;
      }

      setOwnerIdProofFile(file);
      setOwnerIdProofPreview(URL.createObjectURL(file));
    }
  };

  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>, documentType: string) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Document file size must be less than 2MB');
      return;
    }

    const newDocument = {
      documentType: documentType,
      file: file,
      fileName: file.name,
    };

    setDocuments([...documents, newDocument]);
    toast.success('Document added. Will be uploaded on submit.');
  };

  const handleRemoveDocument = async (index: number) => {
    const docToRemove = documents[index];

    // If it's an existing document (has fileId), delete from storage first
    if (docToRemove.fileId && !docToRemove.file) {
      try {
        await uploadService.deleteSingleFile(docToRemove.fileId);
        toast.success(`${docToRemove.documentType} document deleted successfully`);
      } catch (error) {
        console.error('Failed to delete document:', error);
        toast.error(`Failed to delete ${docToRemove.documentType} document`);
        return; // Don't remove from state if delete failed
      }
    }

    // Remove from state
    setDocuments(documents.filter((_, i) => i !== index));

    if (!docToRemove.fileId) {
      toast.success(`${docToRemove.documentType} document removed`);
    }
  };

  const handleBusinessHoursChange = (index: number, field: keyof BusinessHours, value: string | boolean) => {
    const updatedHours = [...businessHours];
    updatedHours[index] = { ...updatedHours[index], [field]: value };
    setBusinessHours(updatedHours);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isEditMode ? 'Edit Business' : 'Add Business'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert severity="info">
              All businesses are submitted for approval and will be reviewed by the admin.
            </Alert>

            {/* Logo Upload */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Business Logo (Max 2MB)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Avatar
                  src={logoPreview}
                  sx={{ width: 80, height: 80 }}
                  variant="rounded"
                />
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<Upload />}
                  size="small"
                  disabled={uploading}
                  sx={{
                    bgcolor: 'success.main',
                    '&:hover': { bgcolor: 'success.dark' },
                    '&:disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                  }}
                >
                  Upload Logo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLogoChange}
                  />
                </Button>
              </Box>
            </Box>

            {/* Basic Information */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="businessName"
                  label="Business Name"
                  value={formik.values.businessName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.businessName && Boolean(formik.errors.businessName)}
                  helperText={formik.touched.businessName && formik.errors.businessName}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="businessUsername"
                  label="Business Username"
                  placeholder="@mybusiness"
                  value={formik.values.businessUsername}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.businessUsername && Boolean(formik.errors.businessUsername)}
                  helperText={formik.touched.businessUsername && formik.errors.businessUsername}
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  freeSolo
                  options={BUSINESS_CATEGORIES as readonly string[]}
                  value={formik.values.businessCategory || ''}
                  onChange={(_, newValue) => {
                    formik.setFieldValue('businessCategory', (newValue || '').trim());
                  }}
                  onInputChange={(_, newInput, reason) => {
                    if (reason === 'input') {
                      formik.setFieldValue('businessCategory', newInput);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched('businessCategory', true)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      name="businessCategory"
                      label="Business Category"
                      placeholder="Search or type your own…"
                      error={formik.touched.businessCategory && Boolean(formik.errors.businessCategory)}
                      helperText={
                        (formik.touched.businessCategory && formik.errors.businessCategory) ||
                        'Pick from the list, or type your own'
                      }
                      required
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="ownerName"
                  label="Owner Name"
                  value={formik.values.ownerName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.ownerName && Boolean(formik.errors.ownerName)}
                  helperText={formik.touched.ownerName && formik.errors.ownerName}
                  required
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              name="description"
              label="Description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
              multiline
              rows={3}
            />

            {/* Contact Information */}
            <Typography variant="subtitle1" fontWeight="bold">
              Contact Information
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="email"
                  label="Business Email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="website"
                  label="Website (Optional)"
                  placeholder="https://example.com"
                  value={formik.values.website}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.website && Boolean(formik.errors.website)}
                  helperText={formik.touched.website && formik.errors.website}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="contactNumber"
                  label="Contact Number"
                  placeholder="9876543210"
                  value={formik.values.contactNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.contactNumber && Boolean(formik.errors.contactNumber)}
                  helperText={formik.touched.contactNumber && formik.errors.contactNumber}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="whatsappNumber"
                  label="WhatsApp Number (Optional)"
                  placeholder="9876543210"
                  value={formik.values.whatsappNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.whatsappNumber && Boolean(formik.errors.whatsappNumber)}
                  helperText={formik.touched.whatsappNumber && formik.errors.whatsappNumber}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="landlineNumber"
                  label="Landline Number (Optional)"
                  placeholder="04423456789"
                  value={formik.values.landlineNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.landlineNumber && Boolean(formik.errors.landlineNumber)}
                  helperText={formik.touched.landlineNumber && formik.errors.landlineNumber}
                />
              </Grid>
            </Grid>

            {/* Address Information */}
            <Typography variant="subtitle1" fontWeight="bold">
              Business Address
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  name="addressLine1"
                  label="Address Line 1"
                  value={formik.values.addressLine1}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.addressLine1 && Boolean(formik.errors.addressLine1)}
                  helperText={formik.touched.addressLine1 && formik.errors.addressLine1}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  name="addressLine2"
                  label="Address Line 2 (Optional)"
                  value={formik.values.addressLine2}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.addressLine2 && Boolean(formik.errors.addressLine2)}
                  helperText={formik.touched.addressLine2 && formik.errors.addressLine2}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  name="city"
                  label="City"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  name="state"
                  label="State"
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  name="pincode"
                  label="Pincode"
                  value={formik.values.pincode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.pincode && Boolean(formik.errors.pincode)}
                  helperText={formik.touched.pincode && formik.errors.pincode}
                  required
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              name="landmark"
              label="Landmark (Optional)"
              value={formik.values.landmark}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.landmark && Boolean(formik.errors.landmark)}
              helperText={formik.touched.landmark && formik.errors.landmark}
            />

            {/* Business Hours */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule /> Business Hours (Optional)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {businessHours.map((hours, index) => (
                  <Box
                    key={hours.day}
                    sx={{
                      p: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {hours.day}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <TextField
                          fullWidth
                          label="Open Time"
                          type="time"
                          size="small"
                          value={hours.openTime || ''}
                          onChange={(e) => handleBusinessHoursChange(index, 'openTime', e.target.value)}
                          disabled={hours.isClosed}
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <TextField
                          fullWidth
                          label="Close Time"
                          type="time"
                          size="small"
                          value={hours.closeTime || ''}
                          onChange={(e) => handleBusinessHoursChange(index, 'closeTime', e.target.value)}
                          disabled={hours.isClosed}
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={hours.isClosed}
                              onChange={(e) => handleBusinessHoursChange(index, 'isClosed', e.target.checked)}
                            />
                          }
                          label="Closed"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Social Media */}
            <Typography variant="subtitle1" fontWeight="bold">
              Social Media Links (Optional)
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="socialMedia.instagram"
                  label="Instagram URL"
                  placeholder="https://instagram.com/..."
                  value={formik.values.socialMedia.instagram}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.socialMedia?.instagram && Boolean(formik.errors.socialMedia?.instagram)}
                  helperText={formik.touched.socialMedia?.instagram && formik.errors.socialMedia?.instagram}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="socialMedia.facebook"
                  label="Facebook URL"
                  placeholder="https://facebook.com/..."
                  value={formik.values.socialMedia.facebook}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.socialMedia?.facebook && Boolean(formik.errors.socialMedia?.facebook)}
                  helperText={formik.touched.socialMedia?.facebook && formik.errors.socialMedia?.facebook}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="socialMedia.youtube"
                  label="YouTube URL"
                  placeholder="https://youtube.com/..."
                  value={formik.values.socialMedia.youtube}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.socialMedia?.youtube && Boolean(formik.errors.socialMedia?.youtube)}
                  helperText={formik.touched.socialMedia?.youtube && formik.errors.socialMedia?.youtube}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="socialMedia.twitter"
                  label="Twitter/X URL"
                  placeholder="https://twitter.com/..."
                  value={formik.values.socialMedia.twitter}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.socialMedia?.twitter && Boolean(formik.errors.socialMedia?.twitter)}
                  helperText={formik.touched.socialMedia?.twitter && formik.errors.socialMedia?.twitter}
                />
              </Grid>
            </Grid>

            {/* Owner ID Proof */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Owner ID Proof (Optional, Max 2MB)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<Upload />}
                  size="small"
                  disabled={uploading}
                  sx={{
                    bgcolor: 'success.main',
                    '&:hover': { bgcolor: 'success.dark' },
                    '&:disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                  }}
                >
                  Upload ID Proof
                  <input
                    ref={ownerIdProofInputRef}
                    type="file"
                    hidden
                    accept="image/*,application/pdf"
                    onChange={handleOwnerIdProofChange}
                  />
                </Button>
                {ownerIdProofPreview && (
                  <Chip
                    label="ID Proof Uploaded"
                    color="success"
                    size="small"
                    onDelete={async () => {
                      // If it's an existing file (has fileId), delete from storage first
                      if (ownerIdProofId && !ownerIdProofFile) {
                        try {
                          await uploadService.deleteSingleFile(ownerIdProofId);
                          toast.success('Owner ID proof deleted successfully');
                        } catch (error) {
                          console.error('Failed to delete owner ID proof:', error);
                          toast.error('Failed to delete owner ID proof');
                          return; // Don't clear state if delete failed
                        }
                      }

                      // Reset file input
                      if (ownerIdProofInputRef.current) {
                        ownerIdProofInputRef.current.value = '';
                      }

                      // Clear state
                      setOwnerIdProofFile(null);
                      setOwnerIdProofPreview('');
                      setOwnerIdProofId('');

                      if (!ownerIdProofId) {
                        toast.success('Owner ID proof removed');
                      }
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Additional Documents */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Business Documents (Optional, Max 2MB each)
              </Typography>
              <Grid container spacing={2}>
                {['GST', 'License', 'Registration', 'Other'].map((docType) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={docType}>
                    <Button
                      variant="contained"
                      component="label"
                      startIcon={<Upload />}
                      size="small"
                      fullWidth
                      disabled={uploading}
                      sx={{
                        bgcolor: 'success.main',
                        '&:hover': { bgcolor: 'success.dark' },
                        '&:disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                      }}
                    >
                      Upload {docType}
                      <input
                        type="file"
                        hidden
                        accept="image/*,application/pdf"
                        onChange={(e) => handleDocumentUpload(e, docType)}
                      />
                    </Button>
                  </Grid>
                ))}
              </Grid>

              {documents.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {documents.map((doc, index) => (
                    <Chip
                      key={index}
                      label={`${doc.documentType}: ${doc.fileName}`}
                      onDelete={() => handleRemoveDocument(index)}
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={isLoading || uploading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || uploading}
          >
            {uploading ? 'Uploading...' : isLoading ? 'Saving...' : isEditMode ? 'Update' : 'Submit for Approval'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
